import { RefObject, useLayoutEffect } from 'react';
import { observeElementResize } from './observeElementResize';

/** Allocate only the space left by real chrome; never hide overflow to fake a fit. */
export function useFittedGameLayout(enabled: boolean, ref: RefObject<HTMLDivElement | null>, renderer?: string) {
  useLayoutEffect(() => {
    if (!enabled || !ref.current) return;
    const main = ref.current;
    const viewport = main.querySelector<HTMLElement>('.digital-map-viewport');
    if (!viewport) return;
    const measure = () => {
      const mainBox = main.getBoundingClientRect();
      const canvasBox = viewport.getBoundingClientRect();
      const bottom = window.visualViewport ? window.visualViewport.offsetTop + window.visualViewport.height : window.innerHeight;
      const overhead = mainBox.height - canvasBox.height;
      const documentTop = mainBox.top + window.scrollY;
      let bottomInset = 0;
      for (let parent = main.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
        const style = getComputedStyle(parent);
        bottomInset += (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0) + (parseFloat(style.marginBottom) || 0);
      }
      const height = Math.max(300, Math.floor(bottom - documentTop - overhead - bottomInset));
      const value = `${height}px`;
      if (main.style.getPropertyValue('--fitted-map-height') !== value) main.style.setProperty('--fitted-map-height', value);
    };
    measure();
    // The dock updates ancestor padding after its resize notification. Observe
    // the enclosing layout too, so that reservation triggers another measure.
    const disconnect = observeElementResize([main, main.closest('.game-screen'),
      document.querySelector('.game-dock'), document.querySelector('.common-header')], measure);
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      disconnect();
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
      main.style.removeProperty('--fitted-map-height');
    };
  }, [enabled, ref, renderer]);
}
